/**
 * Focus Management Utilities
 * 
 * Provides utilities for managing focus in the application,
 * especially for modals, dialogs, and dynamic content.
 */

import { createFocusTrap } from 'focus-trap';

/**
 * Focus Trap Manager for modals and dialogs
 */
export class FocusTrapManager {
  constructor(element, options = {}) {
    this.element = element;
    this.focusTrap = null;
    this.previouslyFocusedElement = null;

    this.options = {
      escapeDeactivates: true,
      clickOutsideDeactivates: false,
      returnFocusOnDeactivate: true,
      allowOutsideClick: true,
      preventScroll: false,
      ...options
    };
  }

  /**
   * Activate the focus trap
   */
  activate() {
    // Save currently focused element
    this.previouslyFocusedElement = document.activeElement;

    // Create and activate focus trap
    if (this.element) {
      try {
        this.focusTrap = createFocusTrap(this.element, this.options);
        this.focusTrap.activate();
      } catch (error) {
        // If focus trap fails (e.g., no focusable elements), log warning but don't break
        console.warn('Focus trap activation warning:', error.message);
        // Silently fail - the dialog will still work, just without focus management
      }
    }
  }

  /**
   * Deactivate the focus trap
   */
  deactivate() {
    if (this.focusTrap) {
      this.focusTrap.deactivate();
      this.focusTrap = null;
    }

    // Restore focus to previously focused element
    if (this.previouslyFocusedElement && this.previouslyFocusedElement.focus) {
      this.previouslyFocusedElement.focus();
    }
  }

  /**
   * Pause the focus trap
   */
  pause() {
    if (this.focusTrap) {
      this.focusTrap.pause();
    }
  }

  /**
   * Unpause the focus trap
   */
  unpause() {
    if (this.focusTrap) {
      this.focusTrap.unpause();
    }
  }
}

/**
 * Simple Focus Manager for saving and restoring focus
 */
export class FocusManager {
  constructor() {
    this.focusStack = [];
  }

  /**
   * Save the currently focused element
   */
  save() {
    this.focusStack.push(document.activeElement);
  }

  /**
   * Restore the last saved focused element
   */
  restore() {
    const element = this.focusStack.pop();
    if (element && element.focus) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        element.focus();
      }, 50);
    }
  }

  /**
   * Clear the focus stack
   */
  clear() {
    this.focusStack = [];
  }
}

/**
 * Get all focusable elements within a container
 * @param {HTMLElement} container - Container element
 * @returns {Array<HTMLElement>} Array of focusable elements
 */
export function getFocusableElements(container) {
  if (!container) return [];

  const selector = [
    'a[href]',
    'area[href]',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[contenteditable]',
    '[tabindex]:not([tabindex^="-"])'
  ].join(',');

  return Array.from(container.querySelectorAll(selector)).filter(el => {
    return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
  });
}

/**
 * Get the first focusable element in a container
 * @param {HTMLElement} container - Container element
 * @returns {HTMLElement|null} First focusable element or null
 */
export function getFirstFocusableElement(container) {
  const elements = getFocusableElements(container);
  return elements.length > 0 ? elements[0] : null;
}

/**
 * Get the last focusable element in a container
 * @param {HTMLElement} container - Container element
 * @returns {HTMLElement|null} Last focusable element or null
 */
export function getLastFocusableElement(container) {
  const elements = getFocusableElements(container);
  return elements.length > 0 ? elements[elements.length - 1] : null;
}

/**
 * Focus the first focusable element in a container
 * @param {HTMLElement} container - Container element
 * @returns {boolean} True if an element was focused
 */
export function focusFirstElement(container) {
  const element = getFirstFocusableElement(container);
  if (element) {
    element.focus();
    return true;
  }
  return false;
}

/**
 * Check if an element is focusable
 * @param {HTMLElement} element - Element to check
 * @returns {boolean} True if element is focusable
 */
export function isFocusable(element) {
  if (!element) return false;

  const tabindex = element.getAttribute('tabindex');
  if (tabindex && parseInt(tabindex) < 0) return false;

  if (element.disabled) return false;

  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') return false;

  return true;
}

/**
 * Focus trap directive for Vue components
 * Usage: v-focus-trap="isActive"
 */
export const FocusTrapDirective = {
  bind(el) {
    el._focusTrapManager = null;
  },

  update(el, binding) {
    const isActive = binding.value;

    if (isActive && !el._focusTrapManager) {
      // Activate focus trap
      el._focusTrapManager = new FocusTrapManager(el, {
        escapeDeactivates: binding.modifiers.escape !== false,
        clickOutsideDeactivates: binding.modifiers.clickOutside === true,
      });
      el._focusTrapManager.activate();
    } else if (!isActive && el._focusTrapManager) {
      // Deactivate focus trap
      el._focusTrapManager.deactivate();
      el._focusTrapManager = null;
    }
  },

  unbind(el) {
    if (el._focusTrapManager) {
      el._focusTrapManager.deactivate();
      el._focusTrapManager = null;
    }
  }
};

/**
 * Auto-focus directive for Vue components
 * Usage: v-auto-focus
 */
export const AutoFocusDirective = {
  inserted(el, binding) {
    const delay = binding.value || 100;
    setTimeout(() => {
      if (el.focus) {
        el.focus();
      } else {
        const focusable = getFirstFocusableElement(el);
        if (focusable) {
          focusable.focus();
        }
      }
    }, delay);
  }
};

export default {
  FocusTrapManager,
  FocusManager,
  getFocusableElements,
  getFirstFocusableElement,
  getLastFocusableElement,
  focusFirstElement,
  isFocusable,
  FocusTrapDirective,
  AutoFocusDirective
};
